import { getPaymentDate } from "@/lib/paymentDate";

export interface ArqueoPayment {
  id: string;
  user_id: string;
  amount: number;
  paid_at: string | null;
  created_at?: string | null;
  description?: string | null;
  payment_method?: string | null;
  is_article_sale?: boolean | null;
  article_id?: string | null;
  profiles?: {
    first_name?: string | null;
    last_name?: string | null;
    username?: string | null;
  } | null;
}

export interface ArticleInfo {
  id: string;
  name: string;
  price: number;
}

export interface ArticleBreakdownRow {
  article_id: string | null;
  name: string;
  quantity: number;
  subtotal: number;
}

export interface ArqueoTotals {
  total: number;
  count: number;
  byMethod: Record<string, number>;
  byCategory: Record<string, number>;
  articlesBreakdown: ArticleBreakdownRow[];
}

export const ARTICLES_CATEGORY_LABEL = "Artículos";
export const NO_METHOD_LABEL = "Sin método";
export const NO_CATEGORY_LABEL = "Sin categoría";

export const getCustomerName = (p: ArqueoPayment): string => {
  const prof = p.profiles;
  if (!prof) return "Cliente";
  const first = prof.first_name?.trim() || "";
  const last = prof.last_name?.trim() || "";
  const full = `${first} ${last}`.trim();
  return full || prof.username || "Cliente";
};

export const splitDescriptionCategories = (desc?: string | null): string[] => {
  if (!desc) return [];
  return desc
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

export const computeTotals = (
  payments: ArqueoPayment[],
  articlesById: Map<string, ArticleInfo>
): ArqueoTotals => {
  const byMethod: Record<string, number> = {};
  const byCategory: Record<string, number> = {};
  const articleAgg = new Map<string, ArticleBreakdownRow>();
  let total = 0;

  for (const p of payments) {
    const amount = Number(p.amount) || 0;
    total += amount;

    const method = p.payment_method?.trim() || NO_METHOD_LABEL;
    byMethod[method] = (byMethod[method] || 0) + amount;

    if (p.is_article_sale) {
      byCategory[ARTICLES_CATEGORY_LABEL] =
        (byCategory[ARTICLES_CATEGORY_LABEL] || 0) + amount;

      const aid = p.article_id || "__unknown__";
      const info = p.article_id ? articlesById.get(p.article_id) : undefined;
      const name = info?.name || p.description || "Artículo";
      const existing = articleAgg.get(aid);
      if (existing) {
        existing.quantity += 1;
        existing.subtotal += amount;
      } else {
        articleAgg.set(aid, {
          article_id: p.article_id ?? null,
          name,
          quantity: 1,
          subtotal: amount,
        });
      }
    } else {
      const cats = splitDescriptionCategories(p.description);
      if (cats.length === 0) {
        byCategory[NO_CATEGORY_LABEL] =
          (byCategory[NO_CATEGORY_LABEL] || 0) + amount;
      } else {
        // Attribute full amount to each listed category? To avoid double counting,
        // attribute to the FIRST category only; remaining are informational.
        const primary = cats[0];
        byCategory[primary] = (byCategory[primary] || 0) + amount;
      }
    }
  }

  const articlesBreakdown = Array.from(articleAgg.values()).sort(
    (a, b) => b.subtotal - a.subtotal
  );

  return {
    total,
    count: payments.length,
    byMethod,
    byCategory,
    articlesBreakdown,
  };
};

export const getPaymentTime = (p: ArqueoPayment): string => {
  // Use created_at for exact time of registration (paid_at can be a date-only override)
  const ts = p.created_at || p.paid_at;
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

export const getPaymentTimeMinutes = (p: ArqueoPayment): number | null => {
  const ts = p.created_at || p.paid_at;
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
};

export const formatYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const isPaymentOnDate = (p: ArqueoPayment, ymd: string): boolean => {
  const pd = getPaymentDate(p);
  return formatYmd(pd) === ymd;
};
